import { z } from 'zod';
import { IsoDateTime, UuidString } from '../../primitives.js';
import { ColumnConfig } from './column-config.js';
import { FilterMatch, FilterRule } from './filter-rule.js';

/**
 * SavedView — сохранённый набор фильтров + вид (BE-S13-4).
 *
 * scope=user — личный; scope=workspace — расшарен (выполняется с RBAC
 * текущего зрителя — счётчик «24 из 47» при урезании, CI inv 10).
 */

export const SavedViewScope = z.enum(['user', 'workspace']);
export type SavedViewScope = z.infer<typeof SavedViewScope>;

export const SavedViewType = z.enum(['list', 'kanban', 'gantt', 'calendar', 'spreadsheet']);
export type SavedViewType = z.infer<typeof SavedViewType>;

/** TD-13 — измерение группировки (расширено label/urgency); BE-2 — author (постановщик). */
export const SavedViewGroupBy = z.enum([
  'state',
  'due',
  'priority',
  'assignee',
  'author',
  'project',
  'label',
  'urgency',
]);
export type SavedViewGroupBy = z.infer<typeof SavedViewGroupBy>;

export const SavedViewFilter = z.object({
  q: z.string().optional(),
  project_ids: z.array(UuidString).optional(),
  state_groups: z.array(z.string()).optional(),
  priorities: z.array(z.string()).optional(),
  subject_types: z.array(z.string()).optional(),
  assignee_user_ids: z.array(UuidString).optional(),
  // BE-2 — фасет «постановщик» (author_user_id) round-trip в saved-view.
  author_user_ids: z.array(UuidString).optional(),
  // BE-4 — фасеты §11.9: метки + «без исполнителя» (round-trip в saved-view).
  label_ids: z.array(UuidString).optional(),
  no_assignee: z.boolean().optional(),
  due_from: z.string().optional(),
  due_to: z.string().optional(),
  overdue_only: z.boolean().optional(),
  // BE-8 — rule-based конструктор (фильтр-карточка FE-35). Сосуществует с
  // legacy-фасетами выше (back-compat); FE мигрирует на match+rules.
  match: FilterMatch.optional(),
  rules: z.array(FilterRule).max(50).optional(),
});
export type SavedViewFilter = z.infer<typeof SavedViewFilter>;

export const SavedView = z.object({
  id: UuidString,
  entity_id: UuidString,
  owner_user_id: UuidString,
  name: z.string().min(1).max(120),
  scope: SavedViewScope,
  view_type: SavedViewType,
  filter: SavedViewFilter,
  group_by: SavedViewGroupBy.nullable(),
  sort_by: z.string().nullable(),
  /** TD-13 — видимость колонок (display_properties); null = все видимы. */
  display_properties: z.array(z.string()).nullable(),
  /** BE-9 — полный конфиг колонок (порядок/ширина/pinned/visible + scope). */
  column_config: ColumnConfig.nullable(),
  /** TD-13 — подгруппировка (Доска swimlanes). */
  sub_group_by: SavedViewGroupBy.nullable(),
  /** TD-13 — направление сортировки. */
  order_dir: z.enum(['asc', 'desc']).nullable(),
  is_pinned: z.boolean(),
  /** Системные preloaded views (после migration 019) нельзя удалять. */
  is_system: z.boolean(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type SavedView = z.infer<typeof SavedView>;
